import type { DiagramDocument } from '../../core/types';
import type { HistoryState } from '../../core/history';
import { validateDocument } from '../../core/validation';
import { normalizeDocument } from '../../core/normalize';

const DB_NAME = 'owdex-draw';
let PERSISTENCE_SCOPE = 'anonymous';

export function setPersistenceScope(scope: string | null): void {
  const clean = String(scope ?? 'anonymous').trim().replace(/[^A-Za-z0-9._:-]+/g, '_').slice(0, 180);
  PERSISTENCE_SCOPE = clean || 'anonymous';
}

function scopedName(base: string): string { return `${base}:${PERSISTENCE_SCOPE}`; }
function scopedKey(base: string): string { return `${base}:${PERSISTENCE_SCOPE}`; }
const DB_VERSION = 4;
const DOCUMENTS_STORE = 'documents';
const RECOVERY_STORE = 'recovery';
const SYNC_OUTBOX_STORE = 'sync-outbox';
const SYNC_STATE_STORE = 'sync-state';
const FALLBACK_KEY = 'owdex-draw:last-document';
const RECOVERY_FALLBACK_KEY = 'owdex-draw:last-recovery';
const SYNC_FALLBACK_KEY = 'owdex-draw:sync-outbox';
const EMERGENCY_FALLBACK_KEY = 'owdex-draw:emergency-snapshot';
const OWNER_KEY_PREFIX = 'owdex-draw:document-owner:';

function ownerKey(documentId: string): string { return scopedKey(`${OWNER_KEY_PREFIX}${documentId}`); }
function writeOwner(documentId: string, ownerScope?: string | null): void {
  if (!ownerScope) return;
  try { localStorage.setItem(ownerKey(documentId), ownerScope); } catch { /* isolamento é best-effort quando storage falha */ }
}
function ownerMatches(documentId: string, ownerScope?: string | null): boolean {
  if (!ownerScope) return true;
  try { return localStorage.getItem(ownerKey(documentId)) === ownerScope; } catch { return false; }
}
function outboxKey(documentId: string, ownerScope?: string | null): string { return ownerScope ? `${ownerScope}:${documentId}` : documentId; }



interface EmergencySnapshot {
  ownerScope?: string | null;
  savedAt: string;
  document: DiagramDocument;
}

function readEmergencySnapshots(ownerScope?: string | null): DiagramDocument[] {
  try {
    const raw = localStorage.getItem(scopedKey(EMERGENCY_FALLBACK_KEY));
    if (!raw) return [];
    const decoded = JSON.parse(raw) as EmergencySnapshot | EmergencySnapshot[];
    const snapshots = Array.isArray(decoded) ? decoded : [decoded];
    return snapshots.flatMap((parsed) => {
      if (!parsed?.document || !validateDocument(parsed.document).valid) return [];
      if (ownerScope && parsed.ownerScope && parsed.ownerScope !== ownerScope) return [];
      const document = normalizeDocument(parsed.document);
      return ownerMatches(document.id, ownerScope) ? [document] : [];
    });
  } catch {
    return [];
  }
}

/**
 * Snapshot síncrono e imediato para sobreviver a fechamento/refresh antes do debounce do IndexedDB.
 * Mantém as cinco versões mais recentes do usuário para reduzir o risco de perda por aba fechada,
 * travamento ou uma gravação local inválida. É complementar ao salvamento durável.
 */
export function saveEmergencySnapshot(document: DiagramDocument, ownerScope?: string | null): boolean {
  try {
    writeOwner(document.id, ownerScope);
    const key = scopedKey(EMERGENCY_FALLBACK_KEY);
    const raw = localStorage.getItem(key);
    let previous: EmergencySnapshot[] = [];
    if (raw) {
      try {
        const decoded = JSON.parse(raw) as EmergencySnapshot | EmergencySnapshot[];
        previous = (Array.isArray(decoded) ? decoded : [decoded]).filter((item) => item?.document?.id);
      } catch { previous = []; }
    }
    const payload: EmergencySnapshot = { ownerScope: ownerScope ?? null, savedAt: new Date().toISOString(), document };
    const next = [payload, ...previous.filter((item) => !(item.document.id === document.id && item.document.updatedAt === document.updatedAt))].slice(0, 5);
    localStorage.setItem(key, JSON.stringify(next));
    return true;
  } catch {
    return false;
  }
}

export interface SyncOutboxRecord {
  id: string;
  ownerScope?: string | null;
  documentId: string;
  baseRevision: string | null;
  localRevision: string;
  queuedAt: string;
  document: DiagramDocument;
}

interface SyncStateRecord {
  id: string;
  serverRevision: string;
  localRevision?: string;
  updatedAt: string;
}

interface RecoveryRecord {
  id: string;
  updatedAt: string;
  past: DiagramDocument[];
  present: DiagramDocument;
  future: DiagramDocument[];
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(scopedName(DB_NAME), DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DOCUMENTS_STORE)) db.createObjectStore(DOCUMENTS_STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(RECOVERY_STORE)) db.createObjectStore(RECOVERY_STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(SYNC_OUTBOX_STORE)) db.createObjectStore(SYNC_OUTBOX_STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(SYNC_STATE_STORE)) db.createObjectStore(SYNC_STATE_STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function normalizeRecovery(record: RecoveryRecord): HistoryState<DiagramDocument> | null {
  if (!record || !validateDocument(record.present).valid) return null;
  const validPast = Array.isArray(record.past) ? record.past.filter((item) => validateDocument(item).valid).slice(-20).map(normalizeDocument) : [];
  const validFuture = Array.isArray(record.future) ? record.future.filter((item) => validateDocument(item).valid).slice(0, 20).map(normalizeDocument) : [];
  return { past: validPast, present: normalizeDocument(record.present), future: validFuture };
}

export async function saveLocal(document: DiagramDocument, ownerScope?: string | null): Promise<void> {
  let indexedDbSaved = false;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(DOCUMENTS_STORE, 'readwrite');
      tx.objectStore(DOCUMENTS_STORE).put(document);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('Transação de salvamento abortada.'));
    });
    db.close();
    indexedDbSaved = true;
    writeOwner(document.id, ownerScope);
  } catch {
    indexedDbSaved = false;
  }

  // Mantém também um espelho síncrono do último documento. IndexedDB continua sendo o repositório
  // principal, mas este espelho evita perder a última alteração em encerramentos abruptos e ajuda
  // a recuperação quando o navegador suspende/aborta uma transação IndexedDB.
  try {
    localStorage.setItem(scopedKey(FALLBACK_KEY), JSON.stringify(document));
    writeOwner(document.id, ownerScope);
  } catch {
    if (!indexedDbSaved) throw new Error('Não foi possível salvar o projeto no armazenamento local do navegador.');
  }
}

export async function saveRecoverySession(history: HistoryState<DiagramDocument>): Promise<void> {
  const record: RecoveryRecord = {
    id: history.present.id,
    updatedAt: new Date().toISOString(),
    past: history.past.slice(-20),
    present: history.present,
    future: history.future.slice(0, 20),
  };
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(RECOVERY_STORE, 'readwrite');
      tx.objectStore(RECOVERY_STORE).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('Transação de recuperação abortada.'));
    });
    db.close();
    return;
  } catch {
    // fallback abaixo
  }
  try {
    localStorage.setItem(scopedKey(RECOVERY_FALLBACK_KEY), JSON.stringify(record));
  } catch {
    // Falha de recuperação não deve interromper a edição normal.
  }
}

export async function loadRecoverySession(id: string): Promise<HistoryState<DiagramDocument> | null> {
  try {
    const db = await openDb();
    const record = await new Promise<RecoveryRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(RECOVERY_STORE, 'readonly');
      const request = tx.objectStore(RECOVERY_STORE).get(id);
      request.onsuccess = () => resolve(request.result as RecoveryRecord | undefined);
      request.onerror = () => reject(request.error);
    });
    db.close();
    if (record) return normalizeRecovery(record);
  } catch {
    // fallback abaixo
  }
  const raw = localStorage.getItem(scopedKey(RECOVERY_FALLBACK_KEY));
  if (!raw) return null;
  try {
    const record = JSON.parse(raw) as RecoveryRecord;
    if (record.id !== id) return null;
    return normalizeRecovery(record);
  } catch {
    return null;
  }
}

export async function clearRecoverySession(id: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(RECOVERY_STORE, 'readwrite');
      tx.objectStore(RECOVERY_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // fallback abaixo
  }
  const raw = localStorage.getItem(scopedKey(RECOVERY_FALLBACK_KEY));
  if (raw) {
    try {
      const record = JSON.parse(raw) as Partial<RecoveryRecord>;
      if (record.id === id) localStorage.removeItem(scopedKey(RECOVERY_FALLBACK_KEY));
    } catch {
      localStorage.removeItem(scopedKey(RECOVERY_FALLBACK_KEY));
    }
  }
}

export async function loadLatestLocal(ownerScope?: string | null): Promise<DiagramDocument | null> {
  const candidates: DiagramDocument[] = [];
  try {
    const db = await openDb();
    const documents = await new Promise<DiagramDocument[]>((resolve, reject) => {
      const tx = db.transaction(DOCUMENTS_STORE, 'readonly');
      const request = tx.objectStore(DOCUMENTS_STORE).getAll();
      request.onsuccess = () => resolve(request.result as DiagramDocument[]);
      request.onerror = () => reject(request.error);
    });
    db.close();
    for (const document of documents) {
      if (!validateDocument(document).valid || !ownerMatches(document.id, ownerScope)) continue;
      candidates.push(normalizeDocument(document));
    }
  } catch {
    // fallbacks abaixo
  }

  try {
    const raw = localStorage.getItem(scopedKey(FALLBACK_KEY));
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (validateDocument(parsed).valid) {
        const document = normalizeDocument(parsed as DiagramDocument);
        if (ownerMatches(document.id, ownerScope)) candidates.push(document);
      }
    }
  } catch {
    // fallback inválido é ignorado
  }

  candidates.push(...readEmergencySnapshots(ownerScope));

  if (candidates.length === 0) return null;
  return candidates.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ?? null;
}


export async function loadLocalDocument(id: string, ownerScope?: string | null): Promise<DiagramDocument | null> {
  const candidates: DiagramDocument[] = [];
  try {
    const db = await openDb();
    const document = await new Promise<DiagramDocument | undefined>((resolve, reject) => {
      const tx = db.transaction(DOCUMENTS_STORE, 'readonly');
      const request = tx.objectStore(DOCUMENTS_STORE).get(id);
      request.onsuccess = () => resolve(request.result as DiagramDocument | undefined);
      request.onerror = () => reject(request.error);
    });
    db.close();
    if (document && validateDocument(document).valid && ownerMatches(document.id, ownerScope)) candidates.push(normalizeDocument(document));
  } catch {
    // fallbacks abaixo
  }

  try {
    const raw = localStorage.getItem(scopedKey(FALLBACK_KEY));
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (validateDocument(parsed).valid) {
        const document = normalizeDocument(parsed as DiagramDocument);
        if (document.id === id && ownerMatches(document.id, ownerScope)) candidates.push(document);
      }
    }
  } catch {
    // fallback inválido é ignorado
  }

  for (const snapshot of readEmergencySnapshots(ownerScope)) if (snapshot.id === id) candidates.push(snapshot);

  try {
    const recovered = await loadRecoverySession(id);
    if (recovered?.present && ownerMatches(recovered.present.id, ownerScope)) candidates.push(recovered.present);
  } catch {
    // recuperação é best-effort
  }

  if (candidates.length === 0) return null;
  return candidates.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ?? null;
}

export async function listLocalDocuments(ownerScope?: string | null): Promise<DiagramDocument[]> {
  try {
    const db = await openDb();
    const documents = await new Promise<DiagramDocument[]>((resolve, reject) => {
      const tx = db.transaction(DOCUMENTS_STORE, 'readonly');
      const request = tx.objectStore(DOCUMENTS_STORE).getAll();
      request.onsuccess = () => resolve(request.result as DiagramDocument[]);
      request.onerror = () => reject(request.error);
    });
    db.close();
    const validDocuments = documents
      .filter((document) => validateDocument(document).valid)
      .filter((document) => ownerMatches(document.id, ownerScope))
      .map(normalizeDocument)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    if (validDocuments.length > 0) return validDocuments;
  } catch {
    // fallback abaixo
  }

  const raw = localStorage.getItem(scopedKey(FALLBACK_KEY));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!validateDocument(parsed).valid) return [];
    const document = normalizeDocument(parsed as DiagramDocument);
    return ownerMatches(document.id, ownerScope) ? [document] : [];
  } catch {
    return [];
  }
}

export async function deleteLocalDocument(id: string, ownerScope?: string | null): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([DOCUMENTS_STORE, RECOVERY_STORE, SYNC_OUTBOX_STORE, SYNC_STATE_STORE], 'readwrite');
      tx.objectStore(DOCUMENTS_STORE).delete(id);
      tx.objectStore(RECOVERY_STORE).delete(id);
      tx.objectStore(SYNC_OUTBOX_STORE).delete(id);
      tx.objectStore(SYNC_OUTBOX_STORE).delete(outboxKey(id, ownerScope));
      tx.objectStore(SYNC_STATE_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // IndexedDB indisponível: os fallbacks representam apenas o último documento.
  }
  const raw = localStorage.getItem(scopedKey(FALLBACK_KEY));
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<DiagramDocument>;
      if (parsed.id === id) localStorage.removeItem(scopedKey(FALLBACK_KEY));
    } catch {
      localStorage.removeItem(scopedKey(FALLBACK_KEY));
    }
  }
  await clearRecoverySession(id);
  try {
    const emergencyKey = scopedKey(EMERGENCY_FALLBACK_KEY);
    const emergency = localStorage.getItem(emergencyKey);
    if (emergency) {
      const decoded = JSON.parse(emergency) as EmergencySnapshot | EmergencySnapshot[];
      const snapshots = (Array.isArray(decoded) ? decoded : [decoded]).filter((item) => item?.document?.id !== id);
      if (snapshots.length) localStorage.setItem(emergencyKey, JSON.stringify(snapshots)); else localStorage.removeItem(emergencyKey);
    }
  } catch { /* não crítico */ }
  try {
    const outboxFallbackKey = scopedKey(SYNC_FALLBACK_KEY);
    const parsed = JSON.parse(localStorage.getItem(outboxFallbackKey) ?? '{}') as Record<string, SyncOutboxRecord>;
    delete parsed[id];
    delete parsed[outboxKey(id, ownerScope)];
    localStorage.setItem(outboxFallbackKey, JSON.stringify(parsed));
  } catch { /* não crítico */ }
  try { localStorage.removeItem(ownerKey(id)); } catch { /* não crítico */ }
}


function normalizeOutboxRecord(record: Partial<SyncOutboxRecord> & { revision?: string }): SyncOutboxRecord | null {
  if (!record || typeof record.documentId !== 'string' || !record.document || !validateDocument(record.document).valid) return null;
  const localRevision = typeof record.localRevision === 'string' ? record.localRevision : typeof record.revision === 'string' ? record.revision : record.document.updatedAt;
  return {
    id: typeof record.id === 'string' ? record.id : record.documentId,
    documentId: record.documentId,
    baseRevision: typeof record.baseRevision === 'string' ? record.baseRevision : null,
    localRevision,
    queuedAt: typeof record.queuedAt === 'string' ? record.queuedAt : new Date().toISOString(),
    ownerScope: typeof record.ownerScope === 'string' && record.ownerScope ? record.ownerScope : null,
    document: normalizeDocument(record.document),
  };
}

async function getSyncState(documentId: string): Promise<SyncStateRecord | null> {
  try {
    const db = await openDb();
    const record = await new Promise<SyncStateRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(SYNC_STATE_STORE, 'readonly');
      const request = tx.objectStore(SYNC_STATE_STORE).get(documentId);
      request.onsuccess = () => resolve(request.result as SyncStateRecord | undefined);
      request.onerror = () => reject(request.error);
    });
    db.close();
    return record ?? null;
  } catch {
    return null;
  }
}

async function getServerRevision(documentId: string): Promise<string | null> {
  return (await getSyncState(documentId))?.serverRevision ?? null;
}

async function putOutboxRecord(record: SyncOutboxRecord): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(SYNC_OUTBOX_STORE, 'readwrite');
    tx.objectStore(SYNC_OUTBOX_STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('Fila de sincronização abortada.'));
  });
  db.close();
}

export interface QueueSyncOptions { force?: boolean; baseRevision?: string | null; }

export async function queueDocumentForSync(document: DiagramDocument, ownerScope?: string | null, options?: QueueSyncOptions): Promise<void> {
  const existing = (await listSyncOutbox(ownerScope)).find((record) => record.documentId === document.id);
  const syncState = existing ? null : await getSyncState(document.id);
  if (!existing && !options?.force && syncState?.localRevision === document.updatedAt) return;
  const hasExplicitBase = Boolean(options && Object.prototype.hasOwnProperty.call(options, 'baseRevision'));
  const baseRevision = hasExplicitBase ? (options?.baseRevision ?? null) : (existing?.baseRevision ?? syncState?.serverRevision ?? await getServerRevision(document.id));
  const record: SyncOutboxRecord = { id: outboxKey(document.id, ownerScope), ownerScope: ownerScope ?? null, documentId: document.id, baseRevision, localRevision: document.updatedAt, queuedAt: new Date().toISOString(), document };
  try {
    await putOutboxRecord(record);
    return;
  } catch {
    // fallback abaixo; a fila é coalescida por documento para não crescer indefinidamente.
  }
  try {
    const current = JSON.parse(localStorage.getItem(scopedKey(SYNC_FALLBACK_KEY)) ?? '{}') as Record<string, SyncOutboxRecord>;
    current[record.id] = record;
    localStorage.setItem(scopedKey(SYNC_FALLBACK_KEY), JSON.stringify(current));
  } catch {
    throw new Error('Não foi possível preparar a alteração para sincronização futura.');
  }
}

export async function listSyncOutbox(ownerScope?: string | null): Promise<SyncOutboxRecord[]> {
  try {
    const db = await openDb();
    const records = await new Promise<Array<SyncOutboxRecord & { revision?: string }>>((resolve, reject) => {
      const tx = db.transaction(SYNC_OUTBOX_STORE, 'readonly');
      const request = tx.objectStore(SYNC_OUTBOX_STORE).getAll();
      request.onsuccess = () => resolve(request.result as Array<SyncOutboxRecord & { revision?: string }>);
      request.onerror = () => reject(request.error);
    });
    db.close();
    return records.map(normalizeOutboxRecord).filter((record): record is SyncOutboxRecord => record !== null).filter((record) => !ownerScope || record.ownerScope === ownerScope).sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
  } catch {
    // fallback abaixo
  }
  try {
    const parsed = JSON.parse(localStorage.getItem(scopedKey(SYNC_FALLBACK_KEY)) ?? '{}') as Record<string, SyncOutboxRecord & { revision?: string }>;
    return Object.values(parsed).map(normalizeOutboxRecord).filter((record): record is SyncOutboxRecord => record !== null).filter((record) => !ownerScope || record.ownerScope === ownerScope).sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
  } catch {
    return [];
  }
}

export async function countPendingSync(ownerScope?: string | null): Promise<number> {
  return (await listSyncOutbox(ownerScope)).length;
}

export async function markDocumentSynced(documentId: string, localRevision: string, acceptedRevision: string, ownerScope?: string | null): Promise<void> {
  const queueKey = outboxKey(documentId, ownerScope);
  try {
    const db = await openDb();
    const current = await new Promise<SyncOutboxRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(SYNC_OUTBOX_STORE, 'readonly');
      const request = tx.objectStore(SYNC_OUTBOX_STORE).get(queueKey);
      request.onsuccess = () => resolve(request.result as SyncOutboxRecord | undefined);
      request.onerror = () => reject(request.error);
    });
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([SYNC_OUTBOX_STORE, SYNC_STATE_STORE], 'readwrite');
      tx.objectStore(SYNC_STATE_STORE).put({ id: documentId, serverRevision: acceptedRevision, localRevision, updatedAt: new Date().toISOString() } satisfies SyncStateRecord);
      if (!current || current.localRevision === localRevision) tx.objectStore(SYNC_OUTBOX_STORE).delete(queueKey);
      else tx.objectStore(SYNC_OUTBOX_STORE).put({ ...current, baseRevision: acceptedRevision });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('Falha ao concluir sincronização local.'));
    });
    db.close();
  } catch {
    // fallback abaixo
  }
  try {
    const parsed = JSON.parse(localStorage.getItem(scopedKey(SYNC_FALLBACK_KEY)) ?? '{}') as Record<string, SyncOutboxRecord>;
    const current = parsed[queueKey];
    if (!current || current.localRevision === localRevision) delete parsed[queueKey];
    else parsed[queueKey] = { ...current, baseRevision: acceptedRevision };
    localStorage.setItem(scopedKey(SYNC_FALLBACK_KEY), JSON.stringify(parsed));
  } catch { /* sincronização remota não interrompe a edição local */ }
}


/** Adota conscientemente uma versão recebida do servidor, limpando a pendência local daquele mesmo ID. */
export async function adoptRemoteDocument(document: DiagramDocument, serverRevision: string, ownerScope?: string | null): Promise<void> {
  if (!validateDocument(document).valid) throw new Error('Documento remoto inválido.');
  const normalized = normalizeDocument(document);
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([DOCUMENTS_STORE, RECOVERY_STORE, SYNC_OUTBOX_STORE, SYNC_STATE_STORE], 'readwrite');
      tx.objectStore(DOCUMENTS_STORE).put(normalized);
      tx.objectStore(RECOVERY_STORE).delete(normalized.id);
      tx.objectStore(SYNC_OUTBOX_STORE).delete(outboxKey(normalized.id, ownerScope));
      tx.objectStore(SYNC_OUTBOX_STORE).delete(normalized.id);
      tx.objectStore(SYNC_STATE_STORE).put({ id: normalized.id, serverRevision, localRevision: normalized.updatedAt, updatedAt: new Date().toISOString() } satisfies SyncStateRecord);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('Falha ao adotar projeto remoto.'));
    });
    db.close();
    writeOwner(normalized.id, ownerScope);
  } catch {
    await saveLocal(normalized, ownerScope);
  }
  try {
    const parsed = JSON.parse(localStorage.getItem(scopedKey(SYNC_FALLBACK_KEY)) ?? '{}') as Record<string, SyncOutboxRecord>;
    delete parsed[outboxKey(normalized.id, ownerScope)];
    delete parsed[normalized.id];
    localStorage.setItem(scopedKey(SYNC_FALLBACK_KEY), JSON.stringify(parsed));
  } catch { /* fallback opcional */ }
}
